import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "The Edge", href: "#about" },
  { label: "Capabilities", href: "#services" },
  { label: "Why Now", href: "#manifesto" },
  { label: "Contact", href: "#contact" },
];

export default function Nav({ brand }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();
  const name = brand?.name || "LIEBER SOURCING";
  const [nameFirst, ...nameRest] = name.split(" ");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href === "#top") return lenis ? lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" });
    if (lenis) lenis.scrollTo(href, { offset: -80 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 bg-white border-b transition-[box-shadow,border-color,padding] duration-300 ${
          scrolled ? "border-gold-subtle shadow-[0_2px_16px_rgba(20,39,58,0.07)] py-2" : "border-transparent py-3"
        }`}
        data-testid="main-nav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 flex items-center justify-between">
          <button onClick={() => go("#top")} className="flex items-center gap-2.5 md:gap-3.5" data-testid="nav-logo" aria-label="Lieber Sourcing home">
            {brand?.logoUrl && <img src={brand.logoUrl} alt="Lieber Sourcing" className="h-11 md:h-14 w-auto" />}
            <span className="font-display font-extrabold text-navy tracking-tight leading-none text-lg sm:text-xl md:text-2xl">
              {nameFirst} {nameRest.length > 0 && <span className="text-barn">{nameRest.join(" ")}</span>}
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-8 lg:gap-10">
            {LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => go(l.href)}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="font-body text-sm font-semibold text-navy hover:text-barn transition-colors duration-200"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => go("#contact")}
              data-testid="nav-cta"
              className="font-body text-sm font-bold px-5 py-2.5 bg-barn text-white rounded-md hover:bg-[#9c3227] transition-colors duration-200"
            >
              Get in Touch
            </button>
          </nav>

          <button className="md:hidden text-navy p-1" onClick={() => setOpen(true)} data-testid="nav-mobile-open" aria-label="Open menu">
            <Menu size={26} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-white md:hidden flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center px-4 py-3 border-b border-gold-subtle">
              {brand?.logoUrl ? <img src={brand.logoUrl} alt="Lieber Sourcing" className="h-12 w-auto" /> : <span className="font-display font-extrabold text-lg text-navy">LIEBER SOURCING</span>}
              <button onClick={() => setOpen(false)} className="text-navy p-1" data-testid="nav-mobile-close" aria-label="Close menu"><X size={26} /></button>
            </div>
            <div className="flex flex-col px-6 py-8 gap-1">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i + 0.08 }}
                  onClick={() => go(l.href)}
                  className="text-left font-display font-bold text-3xl text-navy py-4 border-b border-gold-subtle"
                >
                  {l.label}
                </motion.button>
              ))}
              <button onClick={() => go("#contact")} className="mt-8 font-body font-bold text-base px-6 py-4 bg-barn text-white rounded-md">
                Get in Touch
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
